import React, { useState, useEffect } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import { devportalApi } from "../../../utils/devportalApi";
import Icon from "../../../components/AppIcon";

const AccountOverview = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [analytics, setAnalytics] = useState({
    totalApiCalls: 0,
    messagesSent: 0,
    activeProjects: 0,
    activeApiKeys: 0,
    conversationCount: 0
  });

  useEffect(() => {
    if (user?.id) {
      loadDashboardData();
    }
  }, [user]);

  const loadDashboardData = async () => {
    setLoading(true);
    setError("");
    try {
      // Fetch dashboard summary data
      const summary = await devportalApi.getDashboardSummary();
      
      if (summary?.error) {
        throw new Error(summary.error);
      }
      
      setAnalytics({
        totalApiCalls: summary?.total_requests || 0,
        messagesSent: summary?.messages_sent || 0,
        activeProjects: summary?.active_projects || 0,
        activeApiKeys: summary?.active_api_keys || 0,
        conversationCount: summary?.total_conversations || 0
      });
    } catch (error) {
      console.error("Failed to load dashboard data:", error);
      setError(error?.message || "Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const summaryStats = [
    {
      title: "Total API Calls",
      value: analytics?.totalApiCalls || "0",
      change: "+12%",
      changeType: "positive",
      icon: "Activity",
    },
    {
      title: "Messages Sent",
      value: analytics?.messagesSent || "0",
      change: "+8%",
      changeType: "positive",
      icon: "Send",
    },
    {
      title: "Active Projects",
      value: analytics?.activeProjects || "0",
      change: "+2",
      changeType: "positive",
      icon: "FolderOpen",
    },
    {
      title: "Conversation Count",
      value: analytics?.conversationCount || "0",
      change: "+0",
      changeType: "positive",
      icon: "MessageSquare",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Account Summary Stats */}
      <div className="bg-card rounded-lg shadow-sm border border-border">
        <div className="px-6 py-4 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">
            Account Summary
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Overview of your account activity and performance
          </p>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-4 flex items-center space-x-2 text-destructive bg-destructive/10 p-3 rounded-md">
              <Icon name="AlertCircle" size={16} />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <span className="ml-2 text-muted-foreground">
                Loading analytics...
              </span>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {summaryStats.map((stat, index) => (
                <div key={index} className="bg-muted/20 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Icon
                        name={stat.icon}
                        size={20}
                        className="text-primary"
                      />
                    </div>
                    <span
                      className={`text-xs font-medium px-2 py-1 rounded-full ${
                        stat.changeType === "positive"
                          ? "text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/20"
                          : "text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900/20"
                      }`}
                    >
                      {stat.change}
                    </span>
                  </div>
                  <h4 className="text-sm font-medium text-muted-foreground">
                    {stat.title}
                  </h4>
                  <p className="text-2xl font-bold text-foreground mt-1">
                    {stat.value}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountOverview;
